import { Component, EventEmitter, Input, OnChanges, OnInit, Output, QueryList, ViewChildren, inject } from '@angular/core';
import {Observable} from 'rxjs';

import {FeatureFlags, FeatureFlagService} from '@core';
import {Location, ModalService, NotificationService, TimeService} from '@shared';
import {
    DragDropService,
    EmployeeView,
    ProblemInstanceService,
    RouteKeyNumberComponent,
    RouteKeyNumbers,
    RoutePlanningService,
    TravelTimeTransportService,
    Visit,
    VisitList,
} from 'planner-shared';
import {RouteKeyNumberWeights} from '../../shared/models/route-key-number-weights.model';
import {TravelTimeTransport} from '../../shared/models/travel-time-transport';
import {RoutePlanningExportComponent} from "../export/route-planning-export.component";
import {UntilDestroy, untilDestroyed} from "@ngneat/until-destroy";

@UntilDestroy()
@Component({
    selector: 'app-route-planning-info',
    templateUrl: './route-planning-info.component.html',
    styleUrls: ['./route-planning-info.component.scss'],
    standalone: false
})
export class RoutePlanningInfoComponent implements OnInit, OnChanges {
    private readonly featureFlagService = inject(FeatureFlagService);
    private readonly routePlanningService = inject(RoutePlanningService);
    private readonly timeService = inject(TimeService);
    private readonly problemInstanceService = inject(ProblemInstanceService);
    private readonly travelTimeTransportService = inject(TravelTimeTransportService);
    private readonly dragDropService = inject(DragDropService);
    private readonly notificationService = inject(NotificationService);
    private readonly modalService = inject(ModalService);

    @Input() public set employeeTasks(value: Visit[]) {
        if (value) {
            this._employeeTasks = value;
            this.setTaskLocations();
        }
    }
    @Input() public set employee(value: EmployeeView) {
        if (value) {
            this._employee = value;
            if(this.routeKeyNumberComponent) {
                this.routeKeyNumberComponent.forEach(routeKeyNumberComponent => {
                    routeKeyNumberComponent.reset();
                });
            }
        }
    }
    @Input() public employeeKeyNumbers: RouteKeyNumbers;
    @Input() public saving: boolean;
    @Input() public dragDisabled: boolean;
    @Input() public routes: VisitList;

    @Output() public minimizedViewEmitter = new EventEmitter();
    @Output() public reloadRoutes = new EventEmitter<VisitList>();

    public get employee(): EmployeeView {
        return this._employee;
    }
    public get employeeTasks(): Visit[] {
        return this._employeeTasks;
    }
    public rosterInInstance: boolean;
    public showCancelDropZoneFeature: Observable<boolean>;
    public minimizeViewFeatureEnabled: Observable<boolean>;
    public mapsEnabled$: Observable<boolean>;
    public taskLocations: Location[];
    public mapVisible: boolean;
    public officeLocation$: Observable<Location>;
    public routeKeyNumberWeights: RouteKeyNumberWeights;
    public transportTravelTime: TravelTimeTransport;
    public travelTimeTooltip: string;
    public isViewMinimized = false;
    public isDragging = false;

    private _employee: EmployeeView;
    private _employeeTasks: Visit[];

    @ViewChildren(RouteKeyNumberComponent) private readonly routeKeyNumberComponent: QueryList<RouteKeyNumberComponent>;

    ngOnInit() {
        this.mapsEnabled$ = this.featureFlagService.isEnabled(FeatureFlags.Maps);
        this.officeLocation$ = this.routePlanningService.getOfficeLocation();
        this.routeKeyNumberWeights = <RouteKeyNumberWeights>{
            accurateArrivals: [],
            travelTime: [],
            taskTime: []
        };
        this.problemInstanceService.rosterDepartmentIsPartOfInstance()
            .pipe()
            .subscribe(result => this.rosterInInstance = result.valueOf());
        this.transportTravelTime = this.travelTimeTransportService.resetTransportTravelTime();
        this.minimizeViewFeatureEnabled = this.featureFlagService.isEnabled(FeatureFlags.MinimizeViewLastStep);
        this.showCancelDropZoneFeature = this.featureFlagService.isEnabled(FeatureFlags.CancelDrop);

        this.dragDropService.draggingTask()
            .pipe(untilDestroyed(this))
            .subscribe(task => {
                this.isDragging = !!task;
        });
    }

    ngOnChanges() {
        if (this.employeeKeyNumbers) {
            this.loadWeights();
            const routeKeyNumbers: RouteKeyNumbers[] = [this.employeeKeyNumbers];
            this.travelTimeTransportService.mapToTravelTimeTransport(routeKeyNumbers)
                .subscribe(travelTime => this.transportTravelTime = travelTime);
            this.travelTimeTooltip = this.travelTimeTransportService.formatTransportTooltip(this.transportTravelTime);
        }
    }
    public loadRoutesInfo(routes: VisitList): void {
        this.reloadRoutes.emit(routes);
    }

    public toggleMap(): void {
        this.mapVisible = !this.mapVisible;
    }

    public openExportModal(): void {
        this.modalService.openCustomModal(RoutePlanningExportComponent, () => {
        }, null, null, true);
    }

    public updateViewStatus() {
        this.isViewMinimized = !this.isViewMinimized;
        this.minimizedViewEmitter.emit();
    }

    public cancelDragging() {
        this.dragDropService.setDraggingDisabled(false);
        this.notificationService.info("CANCELLED_DRAG_TOASTER");
    }

    private setTaskLocations(): void {
        this.taskLocations = this.employeeTasks
            .filter(task => task.longitude && task.latitude)
            .map(task => <Location>{ label: task.locationName, longitude: task.longitude, latitude: task.latitude });
    }
    private loadWeights(): void {
        this.routeKeyNumberWeights.accurateArrivals[0] = this.timeService
            .extractHours(this.employeeKeyNumbers.totalTimeBreakingTimeWindowsInSeconds);
        this.routeKeyNumberWeights.accurateArrivals[1] = this.timeService
            .extractMinutes(this.employeeKeyNumbers.totalTimeBreakingTimeWindowsInSeconds);
        this.routeKeyNumberWeights.travelTime[0] = this.timeService
            .extractHours(this.employeeKeyNumbers.totalTravelTimeSeconds);
        this.routeKeyNumberWeights.travelTime[1] = this.timeService
            .extractMinutes(this.employeeKeyNumbers.totalTravelTimeSeconds);
        this.routeKeyNumberWeights.visitContinuity
            = this.employeeKeyNumbers.totalNumberOfPatientsVisitedByEmployeesNotInPrimaryGroup;
        this.routeKeyNumberWeights.correctCompetence
            = this.employeeKeyNumbers.numberOfTasksWithOverqualifiedWork;
        this.routeKeyNumberWeights.taskTime[0] = this.timeService
            .extractHours(this.employeeKeyNumbers.totalTimeForOfficeTasks + this.employeeKeyNumbers.totalPatientTime);
        this.routeKeyNumberWeights.taskTime[1] = this.timeService
            .extractMinutes(this.employeeKeyNumbers.totalTimeForOfficeTasks + this.employeeKeyNumbers.totalPatientTime);
        this.routeKeyNumberWeights.overMaxEmployeeCount
            = this.employeeKeyNumbers.numberOfPatientsVisitedByOverMaxEmployees;
        this.routeKeyNumberWeights.differentRosterTasks
            = this.employeeKeyNumbers.numberOfTasksWithDifferentRosterDepartment;
    }
}
